/**
 * Reference catalog of CRM notification triggers — for admin debug UI only.
 * Mirrors Cloud Functions + client writers; update when notification paths change.
 */

export type NotificationDebugGroup =
  | 'task-ops'
  | 'pdf'
  | 'crm-pipeline'
  | 'portal'
  | 'rfq-billing'
  | 'other';

export interface NotificationDebugRow {
  id: string;
  group: NotificationDebugGroup;
  trigger: string;
  who: string;
  bellType: string;
  email: string;
  slack: string;
  prefs: string;
  source: string;
}

export const NOTIFICATION_DEBUG_GROUPS: { id: NotificationDebugGroup; label: string }[] = [
  { id: 'task-ops', label: 'TASK & OPS' },
  { id: 'pdf', label: 'PDF / FILES' },
  { id: 'crm-pipeline', label: 'CRM & PIPELINE' },
  { id: 'portal', label: 'PORTAL & CLIENT' },
  { id: 'rfq-billing', label: 'RFQ & BILLING' },
  { id: 'other', label: 'OTHER' },
];

export const NOTIFICATION_DEBUG_CATALOG: NotificationDebugRow[] = [
  // ── Task & Ops (user-configurable prefs) ──
  {
    id: 'task-reminder',
    group: 'task-ops',
    trigger: 'Personal task reminder due (Project Hub)',
    who: 'Reminder creator (self)',
    bellType: 'task_reminder (employee/admin/staff)',
    email: 'Architect/engineer only',
    slack: '—',
    prefs: '— (bell always for internal roles)',
    source: 'scheduledTaskReminders',
  },
  {
    id: 'project-task-assigned',
    group: 'task-ops',
    trigger: 'Project task created / new assignee',
    who: 'New assignee(s)',
    bellType: 'ext_task_assigned (architect/engineer) · task_assigned (client UI)',
    email: 'If email on',
    slack: '—',
    prefs: 'onTaskAssigned',
    source: 'onProjectTaskCreated · onProjectTaskUpdated · createTaskAssignmentNotification',
  },
  {
    id: 'ops-task-assigned',
    group: 'task-ops',
    trigger: 'Ops task created / assignee added',
    who: 'Assignee(s)',
    bellType: 'task_assigned (staff) · ext_task_assigned (portal users)',
    email: 'If email on',
    slack: '—',
    prefs: 'onTaskAssigned',
    source: 'onOpsTaskCreated · onOpsTaskUpdated',
  },
  {
    id: 'task-status',
    group: 'task-ops',
    trigger: 'Project or ops task status changes',
    who: 'All assignees (not new assignees same event)',
    bellType: 'ext_status_changed (portal)',
    email: 'If email on',
    slack: '—',
    prefs: 'onTaskStatusChanged',
    source: 'onProjectTaskUpdated · onOpsTaskUpdated',
  },
  {
    id: 'task-completed',
    group: 'task-ops',
    trigger: 'Task marked completed',
    who: 'Assignees',
    bellType: 'ext_task_completed (portal)',
    email: 'If email on',
    slack: '—',
    prefs: 'onTaskCompleted',
    source: 'onProjectTaskUpdated · onOpsTaskUpdated',
  },
  {
    id: 'action-required',
    group: 'task-ops',
    trigger: 'Action required user set on task',
    who: 'Selected user',
    bellType: 'ext_action_required (portal)',
    email: 'If email on',
    slack: '—',
    prefs: 'onActionRequired',
    source: 'onProjectTaskUpdated · onOpsTaskUpdated',
  },
  {
    id: 'project-task-comment',
    group: 'task-ops',
    trigger: 'Comment on project task (not @mention)',
    who: 'Assignees only (excl. author)',
    bellType: 'task_comment · external_comment',
    email: 'Rich email for architects + engineers only when @mentioned; internal staff per onComment',
    slack: '—',
    prefs: 'onComment',
    source: 'onTaskCommentCreated',
  },
  {
    id: 'project-task-mention',
    group: 'task-ops',
    trigger: '@mention in project task comment',
    who: 'Mentioned user(s)',
    bellType: 'task_mention',
    email: 'Rich fallback email for architects + engineers; deep links grant mention-only access',
    slack: '—',
    prefs: 'onMention',
    source: 'onTaskCommentCreated · createTaskMentionNotification (client, avoid dup)',
  },
  {
    id: 'ops-comment',
    group: 'task-ops',
    trigger: 'Comment on ops task (not @mention)',
    who: 'Assignees + collaborators',
    bellType: 'task_comment',
    email: 'If email on',
    slack: '—',
    prefs: 'onComment',
    source: 'onOpsCommentCreated',
  },
  {
    id: 'ops-mention',
    group: 'task-ops',
    trigger: '@mention in ops comment',
    who: 'Mentioned user(s)',
    bellType: 'task_mention',
    email: 'If email on',
    slack: '—',
    prefs: 'onMention',
    source: 'onOpsCommentCreated',
  },
  {
    id: 'lead-todo-assigned',
    group: 'task-ops',
    trigger: 'Lead todo assigned or reassigned',
    who: 'Todo assignee',
    bellType: 'lead_todo_assigned',
    email: '—',
    slack: '—',
    prefs: 'always (no toggle)',
    source: 'notifyLeadTodoAssignee',
  },
  {
    id: 'onboarding-action',
    group: 'task-ops',
    trigger: 'Onboarding checklist step completed → next assignee',
    who: 'Next step assignee',
    bellType: 'onboarding_action_needed',
    email: '—',
    slack: '—',
    prefs: 'always',
    source: 'ProjectHub (client write)',
  },
  {
    id: 'onboarding-slack',
    group: 'task-ops',
    trigger: 'Onboarding step completed',
    who: 'Team (bell only)',
    bellType: '—',
    email: '—',
    slack: '—',
    prefs: 'always',
    source: 'sendPortalNotification · onboarding_step_completed',
  },

  // ── PDF / files ──
  {
    id: 'pdf-comment',
    group: 'pdf',
    trigger: 'PDF / drawing pin comment (broadcast)',
    who: 'Project files and lead documents: nobody (mentions only). Hiring resumes: active admins. Purchasing attachments: active employees and admins.',
    bellType: 'pdf_comment · pdf_external_comment',
    email: 'Rich email for architects + engineers only when @mentioned; internal staff per onComment',
    slack: '—',
    prefs: 'onComment + email on',
    source: 'onPdfAnnotationCreated · dispatchPdfAnnotationInAppNotifications',
  },
  {
    id: 'pdf-mention',
    group: 'pdf',
    trigger: '@mention on PDF annotation',
    who: 'Mentioned user',
    bellType: 'mention',
    email: 'Rich fallback email for architects + engineers; internal staff via task email path',
    slack: '—',
    prefs: 'onMention',
    source: 'dispatchPdfAnnotationInAppNotifications',
  },
  {
    id: 'lead-comment-mention',
    group: 'pdf',
    trigger: '@mention in lead comment thread',
    who: 'Mentioned user',
    bellType: 'mention',
    email: '—',
    slack: '—',
    prefs: 'always',
    source: 'createMentionNotification (client)',
  },
  {
    id: 'plan-review-complete',
    group: 'pdf',
    trigger: 'PM completes a quiet plan review on a drawing',
    who: 'Recipients chosen by PM (smart defaults: uploader, designer, @mentions, project PMs)',
    bellType: 'plan_review_complete',
    email: 'Digest to everyone checked in the complete modal (respects notificationPrefs.email + onComment); delivery logged',
    slack: '—',
    prefs: 'onComment + email on',
    source: 'onPlanReviewUpdated',
  },
  {
    id: 'project-file-version',
    group: 'pdf',
    trigger: 'New version uploaded to project file stream',
    who: 'Admin, employee, staff, designer, engineer (excl. uploader, EAs, architects, shop, and office)',
    bellType: 'project_file_version',
    email: '—',
    slack: '—',
    prefs: 'always',
    source: 'onProjectFileVersionCreated',
  },

  {
    id: 'board-pin-comment',
    group: 'portal',
    trigger: 'Comment or reply on inspiration board pin (not sticky note)',
    who: '@mentioned users · thread participants · assigned designer / project PMs',
    bellType: 'board_mention · board_comment',
    email: 'If email on (onMention for @mentions, onComment for thread/team)',
    slack: '—',
    prefs: 'onMention + onComment + email',
    source: 'onBoardCommentWritten',
  },
  {
    id: 'board-note-mention',
    group: 'portal',
    trigger: '@mention in sticky note on inspiration board',
    who: 'Mentioned user(s)',
    bellType: 'board_mention',
    email: '—',
    slack: '—',
    prefs: 'always (client-side)',
    source: 'notifyBoardMentions (client)',
  },

  // ── CRM & pipeline ──
  {
    id: 'new-lead',
    group: 'crm-pipeline',
    trigger: 'New lead document created',
    who: 'Internal CRM staff (not designers, architects, engineers, or suppliers)',
    bellType: 'new_lead',
    email: '—',
    slack: '—',
    prefs: 'always',
    source: 'onLeadCreated',
  },
  {
    id: 'incoming-sms',
    group: 'crm-pipeline',
    trigger: 'Inbound Sendblue SMS received',
    who: 'Internal CRM staff (not designers, architects, engineers, or suppliers)',
    bellType: 'new_message',
    email: '—',
    slack: '—',
    prefs: 'always',
    source: 'sendblueWebhook',
  },
  {
    id: 'portal-deliverable-approval',
    group: 'crm-pipeline',
    trigger: 'Client approves 3D model, concept preview, or 2D concept drawings in portal',
    who: 'All CRM users except EAs (designers / architects / engineers keep these; suppliers and CPAs have no inbox)',
    bellType: 'portal_deliverable_approval',
    email: '—',
    slack: '#CRM post',
    prefs: 'always',
    source: 'recordPortalDeliverableApproval',
  },
  {
    id: 'deal-won-proposal',
    group: 'crm-pipeline',
    trigger: 'Client signs proposal (portal)',
    who: 'Executives (CEO / COO / CFO / office manager / org-chart executive)',
    bellType: 'deal_won',
    email: '—',
    slack: '#CRM post',
    prefs: 'always',
    source: 'onProposalSigned',
  },
  {
    id: 'deal-won-engagement',
    group: 'crm-pipeline',
    trigger: 'Engagement letter signed',
    who: 'Executives (CEO / COO / CFO / office manager / org-chart executive)',
    bellType: 'deal_won',
    email: '—',
    slack: '—',
    prefs: 'always',
    source: 'EngagementPage (client write)',
  },
  {
    id: 'ops-review-requested',
    group: 'crm-pipeline',
    trigger: 'OPS review requested on proposal',
    who: 'Selected reviewer',
    bellType: 'proposal_ops_review_requested',
    email: '—',
    slack: '#CRM @reviewer',
    prefs: 'always (bell)',
    source: 'onProposalOpsReviewMetaUpdated',
  },
  {
    id: 'ops-review-complete',
    group: 'crm-pipeline',
    trigger: 'OPS review marked complete',
    who: 'Sales rep on deal',
    bellType: 'proposal_ops_review_approved',
    email: '—',
    slack: '#CRM @sales',
    prefs: 'always (bell)',
    source: 'onProposalOpsReviewMetaUpdated',
  },

  // ── Portal & client ──
  {
    id: 'portal-feedback',
    group: 'portal',
    trigger: 'Client leaves note on portal step',
    who: 'All CRM users except EAs (designers / architects / engineers keep these; suppliers and CPAs have no inbox)',
    bellType: 'portal_feedback',
    email: '—',
    slack: '—',
    prefs: 'always',
    source: 'sendPortalNotification',
  },
  {
    id: 'portal-upload',
    group: 'portal',
    trigger: 'Client uploads file to portal',
    who: 'All CRM users except EAs (designers / architects / engineers keep these; suppliers and CPAs have no inbox)',
    bellType: 'portal_upload',
    email: '—',
    slack: '—',
    prefs: 'always',
    source: 'sendPortalNotification',
  },
  {
    id: 'questionnaire-complete',
    group: 'portal',
    trigger: 'Design questionnaire completed',
    who: 'All CRM users except EAs (designers / architects / engineers keep these; suppliers and CPAs have no inbox)',
    bellType: 'questionnaire_completed',
    email: '—',
    slack: '#CRM post',
    prefs: 'always',
    source: 'sendPortalNotification',
  },
  {
    id: 'design-agreement-signed',
    group: 'portal',
    trigger: 'Design agreement / fee checkout signed',
    who: 'Executives (CEO / COO / CFO / office manager / org-chart executive)',
    bellType: 'system',
    email: '—',
    slack: '#CRM post',
    prefs: 'always',
    source: 'createDesignFeeCheckout',
  },
  {
    id: 'design-fee-paid',
    group: 'portal',
    trigger: 'Stripe design fee payment succeeds',
    who: 'Executives (CEO / COO / CFO / office manager / org-chart executive)',
    bellType: 'system',
    email: '—',
    slack: '#CRM post',
    prefs: 'always',
    source: 'designFeeWebhook',
  },

  // ── RFQ & billing ──
  {
    id: 'supplier-rfq',
    group: 'rfq-billing',
    trigger: 'Supplier uploads quote or marks not bidding',
    who: 'Configured RFQ notify users',
    bellType: 'supplier_rfq_upload',
    email: '—',
    slack: '#CRM post',
    prefs: 'always',
    source: 'onRfqSubmissionCreated',
  },
  {
    id: 'quote-approved',
    group: 'rfq-billing',
    trigger: 'Project quote approved for billing',
    who: 'Billing notify user',
    bellType: 'quote_approved',
    email: '—',
    slack: '#CRM post',
    prefs: 'always',
    source: 'approveProjectQuote',
  },
  {
    id: 'bill-approved-to-pay',
    group: 'rfq-billing',
    trigger: 'Payment approved / bill marked ready to pay',
    who: 'Admin + EA (not the approver)',
    bellType: 'billing_payables',
    email: '—',
    slack: '—',
    prefs: 'always',
    source: 'onSupplierBillUpdated',
  },
  {
    id: 'qb-payment',
    group: 'rfq-billing',
    trigger: 'QuickBooks payment matched to lead',
    who: 'Users in paymentNotifyUsers setting',
    bellType: 'payment_received (notifications coll.)',
    email: '—',
    slack: '—',
    prefs: 'appSettings QB config',
    source: 'quickbooksWebhook',
  },
  {
    id: 'capital-plan-mention',
    group: 'other',
    trigger: '@mention a CPA on a capital plan comment or case',
    who: 'Mentioned CPA(s)',
    bellType: 'capital_plan',
    email: 'Rich email with the case, costs, and recent discussion',
    slack: '—',
    prefs: 'always (CPA mention)',
    source: 'onCapitalPlanCommentCreated · onCapitalPlanWritten · notifyCapitalPlanEvent (bell)',
  },
  {
    id: 'bug-squashed',
    group: 'other',
    trigger: 'Disabled — quiet closes (#18); Mark done no longer notifies',
    who: '—',
    bellType: 'bug_squashed',
    email: '—',
    slack: '—',
    prefs: 'off',
    source: 'updateCrmFeedback / closeCrmFeedback (no-op)',
  },
];

export const NOTIFICATION_DEBUG_PATH = '/debug/notifications';
